import React from 'react';
import { Box, Typography, LinearProgress, Fade } from '@mui/material';
import { keyframes } from '@mui/system';

// Custom animations
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
`;

const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LoadingConfigScreen: React.FC = () => {
  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw',
          background: '#ffffff',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 9999,
        }}
      >
        {/* Logo/Icon Container */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #7eb3a3 0%, #5a9085 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 3,
            animation: `${pulseAnimation} 2s ease-in-out infinite`,
            boxShadow: '0 10px 30px rgba(126, 179, 163, 0.3)',
            border: '3px solid rgba(126, 179, 163, 0.1)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              fontFamily: 'Arial, sans-serif',
            }}
          >
            C
          </Typography>
        </Box>

        {/* Loading Text */}
        <Box
          sx={{
            textAlign: 'center',
            marginBottom: 4,
            animation: `${fadeInUp} 0.8s ease-out`,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: '#2c3e50',
              fontWeight: 600,
              marginBottom: 1,
              letterSpacing: '0.5px',
            }}
          >
            Loading Configuration
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#7f8c8d',
              fontWeight: 400,
            }}
          >
            Setting up your workspace...
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box
          sx={{
            width: 300,
            marginBottom: 2,
            animation: `${fadeInUp} 1s ease-out`,
          }}
        >
          <LinearProgress
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(126, 179, 163, 0.2)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: 'linear-gradient(90deg, #7eb3a3 0%, #5a9085 100%)',
              },
            }}
          />
        </Box>

        {/* Status Text */}
        <Typography
          variant="caption"
          sx={{
            color: '#95a5a6',
            fontWeight: 500,
            animation: `${fadeInUp} 1.2s ease-out`,
            fontSize: '0.875rem',
          }}
        >
          Please wait while we prepare everything for you
        </Typography>

        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(126, 179, 163, 0.1)',
            animation: `${pulseAnimation} 3s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            right: '15%',
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'rgba(126, 179, 163, 0.05)',
            animation: `${pulseAnimation} 2.5s ease-in-out infinite reverse`,
          }}
        />
      </Box>
    </Fade>
  );
};

export default LoadingConfigScreen;
